/**
 * Whack Friday strip (Paul's Priority list, row 9, 4 Nov 2026).
 *
 * Framework-free ES module, loaded from snippets/rogue-drop-strip.liquid.
 * CSS shows the "live" or the "next drop" line by <html data-rogue-drop>, which
 * assets/rogue-freeship.js keeps right to the second. This script keeps the
 * date in the "next drop" line right on a page Shopify cached earlier, working
 * it out from the same drop times (script[data-rogue-freeship-data], epoch
 * seconds), and hides the strip once no drop is left. It re-runs whenever
 * data-rogue-drop changes, so the date moves on the moment a drop ends.
 *
 * Unit tested by tests/rogue-drop-strip.test.js.
 */

/** Start (epoch seconds) of the first drop that has not started, or null. */
export function nextDropStart(windows, nowMs) {
  const now = nowMs / 1000;
  let next = null;
  for (const w of Array.isArray(windows) ? windows : []) {
    if (!Array.isArray(w) || !Number.isFinite(w[0]) || !Number.isFinite(w[1]) || w[1] < w[0]) continue;
    if (w[0] > now && (next === null || w[0] < next)) next = w[0];
  }
  return next;
}

/** Whether a drop is live at `nowMs` (end second inclusive). */
export function isLive(windows, nowMs) {
  const now = nowMs / 1000;
  return (Array.isArray(windows) ? windows : []).some((w) => Array.isArray(w) && now >= w[0] && now <= w[1]);
}

/**
 * What the strip shows: live, before (a drop is still to come) or none.
 * @returns {'live'|'before'|'none'}
 */
export function stripState(windows, nowMs) {
  if (isLive(windows, nowMs)) return 'live';
  return nextDropStart(windows, nowMs) === null ? 'none' : 'before';
}

/**
 * "Friday 6 November" and "10pm" for a start time, in UK time whatever the shopper's clock says.
 * @param {number} startSeconds
 * @returns {{date: string, time: string}}
 */
export function formatDrop(startSeconds) {
  const d = new Date(startSeconds * 1000);
  const part = (opts, type) => new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', ...opts }).formatToParts(d).find((p) => p.type === type)?.value ?? '';
  const date = `${part({ weekday: 'long' }, 'weekday')} ${part({ day: 'numeric' }, 'day')} ${part({ month: 'long' }, 'month')}`;
  const clock = { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' };
  const hour24 = Number(part(clock, 'hour'));
  const minute = part(clock, 'minute');
  const hour = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return { date, time: `${hour}${minute === '00' ? '' : `:${minute}`}${hour24 >= 12 ? 'pm' : 'am'}` };
}

/** Fills [date] and [time] in the editor's text. */
export function fillTemplate(template, startSeconds) {
  const { date, time } = formatDrop(startSeconds);
  return String(template).replaceAll('[date]', date).replaceAll('[time]', time);
}

// Browser only: Node (the unit tests) has no document.
if (typeof document !== 'undefined') {
  const apply = () => {
    const strip = document.querySelector('rogue-drop-strip');
    if (!strip) return;
    let data;
    try {
      data = JSON.parse(document.querySelector('script[data-rogue-freeship-data]')?.textContent || 'null');
    } catch {
      data = null;
    }
    if (!data) return;
    const now = Date.now();
    const state = stripState(data.windows, now);
    strip.hidden = state === 'none';
    const next = nextDropStart(data.windows, now);
    const before = strip.querySelector('[data-rogue-strip-before]');
    if (before && next !== null && strip.dataset.template) before.textContent = fillTemplate(strip.dataset.template, next);
  };
  apply();
  new MutationObserver(apply).observe(document.documentElement, { attributes: true, attributeFilter: ['data-rogue-drop'] });
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) apply();
  });
  window.addEventListener('pageshow', apply);
}
